import { Toys } from '@mui/icons-material';
import { Stack, Typography, Box, Modal } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { BACKGROUND_GRADIENT } from '../../theme/gaimcontrolTheme';

interface Circle {
  id: number;
  x: number;
  y: number;
  radius: number;
  vx: number; // velocity x
  vy: number; // velocity y
  hasObject: boolean;
}

interface Square {
  id: number;
  x: number;
  y: number;
  size: number;
  vx: number; // velocity x
  vy: number; // velocity y
}

interface PassingLine {
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  color: string;      // black or red
  createdAt: number;  // timestamp for fading out
}

interface BgPolygon {
  points: { x: number; y: number }[];
  color: string;
  initialShade: number;
}

interface Gorilla {
  y: number;
  hasStarted: boolean;
  startTime: number;
}

interface PassingGameProps {
  circleCount?: number;
  squareCount?: number;
  gameDuration?: number;
  showGorilla?: boolean;
}

const CANVAS_WIDTH = 600;
const CANVAS_HEIGHT = 400;
const PLAYER_SIZE = 50
const PASS_LINE_DURATION = 2000; // 2 seconds
const POSSESSION_LIMIT = 10;     // 10 seconds

const PassingGame: React.FC<PassingGameProps> = ({
  circleCount = 10,
  squareCount = 10,
  gameDuration = 60,
  showGorilla = true,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // State to track the 60-second countdown
  const [timeLeft, setTimeLeft] = useState<number>(gameDuration);
  // State to track the pass counter
  const [passCount, setPassCount] = useState<number>(0);
  // State to determine if the game is over
  const [isGameOver, setIsGameOver] = useState<boolean>(false);

  // The circles and squares are stored in refs so we can update them
  // without triggering a re-render on every frame.
  const circlesRef = useRef<Circle[]>([]);
  const squaresRef = useRef<Square[]>([]);

  // Which circle currently holds the ball?
  const [selectedCircleId, setSelectedCircleId] = useState<number | null>(null);

  // For drawing lines over time
  const [passingLines, setPassingLines] = useState<PassingLine[]>([]);

  /**
   * Keep track of when the current possession started.
   * If the circle holds the ball for more than 10s,
   * we penalize once (-1 passCount) and reset it.
   */
  const possessionStartRef = useRef<number>(Date.now());

  const bgPolygonsRef = useRef<BgPolygon[]>([]);

  const gorillaRef = useRef<Gorilla>({
    y: -100,
    hasStarted: false,
    startTime: 0,
  });

  const gorillaImageRef = useRef<HTMLImageElement | null>(null);

  // ---------------------------
  // Helper: Initialize circles & squares at mount
  // ---------------------------
  useEffect(() => {
    // Create random circles
    const newCircles: Circle[] = Array.from({ length: circleCount }, (_, i) => {
      return {
        id: i,
        x: Math.random() * (CANVAS_WIDTH - 50) + 25,
        y: Math.random() * (CANVAS_HEIGHT - 50) + 25,
        radius: PLAYER_SIZE / 2,
        vx: (Math.random() - 0.5) * 3,
        vy: (Math.random() - 0.5) * 3,
        hasObject: false,
      };
    });

    // Randomly pick which circle starts with the black dot
    const starterIndex = Math.floor(Math.random() * circleCount);
    newCircles[starterIndex].hasObject = true;
    setSelectedCircleId(newCircles[starterIndex].id);

    circlesRef.current = newCircles;

    // Create random squares
    const newSquares: Square[] = Array.from({ length: squareCount }, (_, i) => {
      return {
        id: i,
        x: Math.random() * (CANVAS_WIDTH - 40) + 20,
        y: Math.random() * (CANVAS_HEIGHT - 40) + 20,
        size: PLAYER_SIZE,
        vx: (Math.random() - 0.5) * 2,
        vy: (Math.random() - 0.5) * 2,
      };
    });
    squaresRef.current = newSquares;

    // Initialize possession start time
    possessionStartRef.current = Date.now();

    const columns = 8;
    const rows = 6;
    const cellWidth = CANVAS_WIDTH / columns;
    const cellHeight = CANVAS_HEIGHT / rows;
    const newBgPolys: BgPolygon[] = [];

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < columns; c++) {
        const x1 = c * cellWidth;
        const y1 = r * cellHeight;
        const x2 = (c + 1) * cellWidth;
        const y2 = (r + 1) * cellHeight;
        const corners = [
          { x: x1 + Math.random() * 0.3 * cellWidth,  y: y1 + Math.random() * 0.3 * cellHeight },
          { x: x2 - Math.random() * 0.3 * cellWidth,  y: y1 + Math.random() * 0.3 * cellHeight },
          { x: x2 - Math.random() * 0.3 * cellWidth,  y: y2 - Math.random() * 0.3 * cellHeight },
          { x: x1 + Math.random() * 0.3 * cellWidth,  y: y2 - Math.random() * 0.3 * cellHeight },
        ];
        const shade = 60 + Math.floor(Math.random() * 180);
        newBgPolys.push({
          points: corners,
          color: `rgb(${shade},${shade},${shade})`,
          initialShade: shade,
        });
      }
    }
    bgPolygonsRef.current = newBgPolys;

    const img = new Image();
    img.src = "/elephant_blue.png";
    gorillaImageRef.current = img;
  }, []);

  // ---------------------------
  // Movement: update positions of circles and squares
  // ---------------------------
  const updatePositions = () => {
    // Move circles
    circlesRef.current.forEach((circle) => {
      circle.x += circle.vx;
      circle.y += circle.vy;

      // Bounce off walls
      if (circle.x - circle.radius < 0 || circle.x + circle.radius > CANVAS_WIDTH) {
        circle.vx *= -1;
      }
      if (circle.y - circle.radius < 0 || circle.y + circle.radius > CANVAS_HEIGHT) {
        circle.vy *= -1;
      }
    });

    // Move squares
    squaresRef.current.forEach((square) => {
      square.x += square.vx;
      square.y += square.vy;

      // Bounce off walls
      if (square.x < 0 || square.x + square.size > CANVAS_WIDTH) {
        square.vx *= -1;
      }
      if (square.y < 0 || square.y + square.size > CANVAS_HEIGHT) {
        square.vy *= -1;
      }
    });
  };

  // ---------------------------
  // Helper: line-rectangle intersection check
  // ---------------------------
  const lineIntersectsSquare = (
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    square: Square
  ): boolean => {
    // Square corners
    const left = square.x;
    const right = square.x + square.size;
    const top = square.y;
    const bottom = square.y + square.size;

    // Helper function: line-line intersection
    const linesIntersect = (
      Ax: number, Ay: number,
      Bx: number, By: number,
      Cx: number, Cy: number,
      Dx: number, Dy: number
    ): boolean => {
      const denominator = (Bx - Ax) * (Dy - Cy) - (By - Ay) * (Dx - Cx);
      if (denominator === 0) return false; // parallel or coincident

      const r = ((Ay - Cy) * (Dx - Cx) - (Ax - Cx) * (Dy - Cy)) / denominator;
      const s = ((Ay - Cy) * (Bx - Ax) - (Ax - Cx) * (By - Ay)) / denominator;

      return r >= 0 && r <= 1 && s >= 0 && s <= 1;
    };

    // Check each edge of the square:
    // Edge 1: (left, top) to (right, top)
    if (linesIntersect(x1, y1, x2, y2, left, top, right, top)) return true;
    // Edge 2: (right, top) to (right, bottom)
    if (linesIntersect(x1, y1, x2, y2, right, top, right, bottom)) return true;
    // Edge 3: (right, bottom) to (left, bottom)
    if (linesIntersect(x1, y1, x2, y2, right, bottom, left, bottom)) return true;
    // Edge 4: (left, bottom) to (left, top)
    if (linesIntersect(x1, y1, x2, y2, left, bottom, left, top)) return true;

    return false;
  };

  // ---------------------------
  // Rendering: draw everything on canvas
  // ---------------------------
  const draw = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    bgPolygonsRef.current.forEach(poly => {
      const fraction = showGorilla ? (gameDuration - timeLeft) / gameDuration : 0;
      const newShade = Math.round(poly.initialShade + fraction * (127 - poly.initialShade));
      ctx.beginPath();
      poly.points.forEach((pt, idx) => {
        if (idx === 0) ctx.moveTo(pt.x, pt.y);
        else ctx.lineTo(pt.x, pt.y);
      });
      ctx.closePath();
      ctx.fillStyle = `rgb(${newShade},${newShade},${newShade})`;
      ctx.fill();
    });

    if (showGorilla && gorillaRef.current.hasStarted && gorillaImageRef.current) {
      const gorillaX = CANVAS_WIDTH / 2 - 25;
      ctx.drawImage(gorillaImageRef.current, gorillaX, gorillaRef.current.y, 50, 80);
    }

    // Draw squares (blue)
    squaresRef.current.forEach((square) => {
      ctx.fillStyle = "blue";
      ctx.fillRect(square.x, square.y, square.size, square.size);
    });

    // Draw circles (red)
    circlesRef.current.forEach((circle) => {
      ctx.beginPath();
      ctx.arc(circle.x, circle.y, circle.radius, 0, 2 * Math.PI);
      ctx.fillStyle = "red";
      ctx.fill();

      // If circle has the black dot, draw the dot
      if (circle.hasObject) {
        ctx.beginPath();
        ctx.arc(circle.x, circle.y, circle.radius / 2, 0, 2 * Math.PI);
        ctx.fillStyle = "black";
        ctx.fill();
      }
    });

    // Draw passing lines (fade them within 2 seconds)
    const now = Date.now();
    const aliveLines: PassingLine[] = [];
    passingLines.forEach((line) => {
      const age = now - line.createdAt;
      if (age < PASS_LINE_DURATION) {
        const alpha = 1 - age / PASS_LINE_DURATION;
        ctx.strokeStyle = `rgba(${line.color === "red" ? "255,0,0" : "0,0,0"}, ${alpha.toFixed(3)})`;
        ctx.lineWidth = 3; // pass line width
        ctx.beginPath();
        ctx.moveTo(line.fromX, line.fromY);
        ctx.lineTo(line.toX, line.toY);
        ctx.stroke();
        aliveLines.push(line);
      }
    });

    // Update state with lines still visible
    if (aliveLines.length !== passingLines.length) {
      setPassingLines(aliveLines);
    }
  };

  // ---------------------------
  // Handle passing logic (user clicks on a circle)
  // ---------------------------
  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (isGameOver) return;
    const cw = canvasRef.current!.width;
    const ch = canvasRef.current!.height;
    const scaleX = cw / canvasRef.current!.clientWidth;
    const scaleY = ch / canvasRef.current!.clientHeight;
    const clickX = e.nativeEvent.offsetX * scaleX;
    const clickY = e.nativeEvent.offsetY * scaleY;

    // Find if user clicked a circle
    let clickedCircle: Circle | null = null;
    for (const circle of circlesRef.current) {
      const dx = clickX - circle.x;
      const dy = clickY - circle.y;
      if (Math.sqrt(dx * dx + dy * dy) <= circle.radius) {
        clickedCircle = circle;
        break;
      }
    }

    // If no circle was clicked, do nothing
    if (!clickedCircle) return;

    // If the clicked circle is the same that has the object, do nothing
    if (clickedCircle.id === selectedCircleId) {
      return;
    }

    // Find the circle that currently has the object
    const fromCircle = circlesRef.current.find(c => c.id === selectedCircleId);
    if (!fromCircle) return;

    // Check line-squares intersection
    const passLineBlocked = squaresRef.current.some((sq) =>
      clickedCircle && lineIntersectsSquare(
        fromCircle.x,
        fromCircle.y,
        clickedCircle.x,
        clickedCircle.y,
        sq
      )
    );

    // Determine the color of the pass line (red if blocked, else black)
    const lineColor = passLineBlocked ? "red" : "black";
    if (!clickedCircle) return;
    const toX = clickedCircle.x;
    const toY = clickedCircle.y;

    // Create a new passing line for visual feedback
    setPassingLines((lines) => [
      ...lines,
      {
        fromX: fromCircle.x,
        fromY: fromCircle.y,
        toX: toX,
        toY: toY,
        color: lineColor,
        createdAt: Date.now(),
      },
    ]);

    // If pass is blocked => pass count - 1
    if (passLineBlocked) {
      setPassCount((count) => count - 1);
    } else {
      // If pass is successful => pass count + 1
      setPassCount((count) => count + 1);
    }

    // Transfer object
    fromCircle.hasObject = false;
    clickedCircle.hasObject = true;
    setSelectedCircleId(clickedCircle.id);

    // Reset possession timer
    possessionStartRef.current = Date.now();
  };

  // ---------------------------
  // Game loop: requestAnimationFrame
  // ---------------------------
  useEffect(() => {
    let animationId: number;

    const gameLoop = () => {
      // Update positions
      updatePositions();

      // Check if the current circle has exceeded the 10s limit
      if (!isGameOver) {
        const now = Date.now();
        const elapsed = now - possessionStartRef.current;
        if (elapsed >= POSSESSION_LIMIT * 1000) {
          // Decrement passCount by 1
          setPassCount((count) => count - 1);

          // Reset possession timer
          possessionStartRef.current = now;
        }
      }

      // 20 seconds into the game, the gorilla starts crossing
      if (showGorilla && !gorillaRef.current.hasStarted && timeLeft <= gameDuration - 20) {
        gorillaRef.current.hasStarted = true;
        gorillaRef.current.startTime = performance.now();
      }
      if (showGorilla && gorillaRef.current.hasStarted) {
        const elapsed = (performance.now() - gorillaRef.current.startTime) / 1000;
        const fraction = elapsed / 20; // 20s crossing
        gorillaRef.current.y = fraction < 1 ? (fraction * (CANVAS_HEIGHT + 100) - 100) : CANVAS_HEIGHT;
      }

      // Draw everything
      draw();
      animationId = requestAnimationFrame(gameLoop);
    };

    animationId = requestAnimationFrame(gameLoop);

    return () => {
      cancelAnimationFrame(animationId);
    };
  }, [draw, isGameOver]);

  // ---------------------------
  // Countdown timer
  // ---------------------------
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setIsGameOver(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // ---------------------------
  // Render
  // ---------------------------
  return (
    <Stack style={{ textAlign: "center", height: "100%", display: "flex", flexDirection: "column" }}>
      <Typography variant='h2'>Passing Game</Typography>
      <Typography variant='body2'>Time Left: {timeLeft}</Typography>
      <Typography variant='body2'>Possession Time Left: {Math.round(POSSESSION_LIMIT - (Date.now() - possessionStartRef.current)/1000)}</Typography>
      <Typography variant='body2'>Successful Passes: {passCount}</Typography>
      <Box style={{ flex: 1 }}>
        <canvas
          ref={canvasRef}
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
          style={{ border: "1px solid black", width: "80%", height: "80%" }}
          onClick={handleCanvasClick}
        />
      </Box>
      <Modal open={isGameOver} onClose={() => {}}>
        <Box height={"100%"} display={"flex"} justifyContent={"center"} alignItems={"center"}>
          <Box sx={{ textAlign: "center", margin: "auto", opacity: 1, background: BACKGROUND_GRADIENT, p: 10, borderRadius: 5 }}>
            <Typography variant="h2">Time&apos;s Up!</Typography>
            <Typography variant="body1">Your total successful passes: {passCount}</Typography>
            {
              showGorilla && (
                <div>
                  <Typography>But did you notice an elephant in the room?</Typography>
                  <Typography>And background tiles converging to the same colour?</Typography>
                </div>
              )
            }
          </Box>
        </Box>
      </Modal>
    </Stack>
  );
};

export default PassingGame;
